import React, { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import {
  Send, Search, Star, Clock, CheckCircle, XCircle, X,
  User, ThumbsUp, ThumbsDown,
  Briefcase, GraduationCap, HeartHandshake, Eye,
  MapPin, AlertTriangle, Calendar, Mail, Phone,
} from 'lucide-react'
import toast from 'react-hot-toast'
import type { FeedbackRequest, HiredAlumniForFeedback, AlumniEmployerFeedback, FeedbackRelationship } from '../types'
import { EXPERIENCE_LEVEL_LABELS, SALARY_LABELS } from '../constants/profileOptions'

// ─── Helpers ──────────────────────────────────────────────────
function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function initials(first?: string, last?: string) {
  return ((first?.charAt(0) ?? '') + (last?.charAt(0) ?? '')).toUpperCase() || '?'
}

function fullName(first?: string, last?: string) {
  return `${first ?? ''} ${last ?? ''}`.trim() || 'Unknown'
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  pending:   { label: 'Pending',   color: 'text-yellow-700', bg: 'bg-yellow-100', icon: <Clock       className="w-3.5 h-3.5" /> },
  accepted:  { label: 'Accepted',  color: 'text-blue-700',   bg: 'bg-blue-100',   icon: <CheckCircle className="w-3.5 h-3.5" /> },
  declined:  { label: 'Declined',  color: 'text-red-700',    bg: 'bg-red-100',    icon: <XCircle     className="w-3.5 h-3.5" /> },
  completed: { label: 'Completed', color: 'text-green-700',  bg: 'bg-green-100',  icon: <CheckCircle className="w-3.5 h-3.5" /> },
}

const RELATIONSHIP_LABELS: Record<string, string> = { employee: 'Employee', intern: 'Intern' }

const TYPE_LABELS: Record<string, string> = {
  full_time: 'Full Time', part_time: 'Part Time', contract: 'Contract',
  internship: 'Internship', freelance: 'Freelance',
}

// ─── Star rating display ──────────────────────────────────────
const Stars: React.FC<{ value: number }> = ({ value }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map(i => (
      <Star key={i} className={`w-4 h-4 ${i <= value ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
    ))}
  </div>
)

// ─── Request Modal ────────────────────────────────────────────
// ─── View Hired Alumni Detail Modal ────────────────────────────
interface ViewHiredAlumniModalProps {
  alumni: HiredAlumniForFeedback
  onClose: () => void
  onRequestFeedback: () => void
}

const ViewHiredAlumniModal: React.FC<ViewHiredAlumniModalProps> = ({ alumni, onClose, onRequestFeedback }) => {
  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {alumni.profile_picture ? (
              <img src={alumni.profile_picture} className="w-11 h-11 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-700 flex-shrink-0">
                {initials(alumni.first_name, alumni.last_name)}
              </div>
            )}
            <div className="min-w-0">
              <h3 className="text-base font-bold text-gray-900 truncate">{fullName(alumni.first_name, alumni.last_name)}</h3>
              <p className="text-xs text-gray-500">{alumni.program}{alumni.year_graduated ? ` · Class of ${alumni.year_graduated}` : ''}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition flex-shrink-0"><X className="w-5 h-5 text-gray-400" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* ─ Alumni info ─ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Alumni Info</h4>
              <div className="space-y-1.5 text-sm">
                {alumni.email && (
                  <p className="flex items-center gap-2 text-gray-600">
                    <Mail className="w-4 h-4 text-blue-400 flex-shrink-0" /> {alumni.email}
                  </p>
                )}
                {alumni.phone && (
                  <p className="flex items-center gap-2 text-gray-600">
                    <Phone className="w-4 h-4 text-blue-400 flex-shrink-0" /> {alumni.phone}
                  </p>
                )}
                {alumni.department && (
                  <p className="flex items-center gap-2 text-gray-600">
                    <GraduationCap className="w-4 h-4 text-blue-400 flex-shrink-0" /> {alumni.department}
                  </p>
                )}
                {alumni.hired_at && (
                  <p className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    Hired on <span className="font-medium text-gray-900 ml-1">{formatDate(alumni.hired_at)}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Alumni skills */}
            {alumni.skills && alumni.skills.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Skills</h4>
                <div className="flex flex-wrap gap-1.5">
                  {alumni.skills.map((s: string) => (
                    <span key={s} className="bg-blue-50 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-md">{s}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ─ Job posting ─ */}
          <div className="border border-gray-100 rounded-xl bg-gray-50 p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <h4 className="text-sm font-semibold text-gray-800">Job Position</h4>
              {alumni.job_deleted ? (
                <span className="flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full flex-shrink-0">
                  <AlertTriangle className="w-3 h-3" /> Deleted
                </span>
              ) : alumni.job_status === 'closed' ? (
                <span className="flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full flex-shrink-0">
                  <XCircle className="w-3 h-3" /> Closed
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full flex-shrink-0">
                  <CheckCircle className="w-3 h-3" /> Active
                </span>
              )}
            </div>

            <div>
              <p className="font-bold text-gray-900 text-base">{alumni.job_title}</p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-1.5">
                {alumni.employment_type && (
                  <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                    {TYPE_LABELS[alumni.employment_type] ?? alumni.employment_type}
                  </span>
                )}
                {alumni.experience_level && (
                  <span>{EXPERIENCE_LEVEL_LABELS[alumni.experience_level as keyof typeof EXPERIENCE_LEVEL_LABELS] ?? alumni.experience_level}</span>
                )}
                {alumni.job_location && (
                  <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3 text-gray-400" />{alumni.job_location}</span>
                )}
                {alumni.salary_range && (
                  <span className="text-green-700 font-medium">{SALARY_LABELS[alumni.salary_range] ?? alumni.salary_range}</span>
                )}
                {alumni.job_deadline && (
                  <span className={`flex items-center gap-0.5 font-medium ${
                    new Date(alumni.job_deadline) < new Date() ? 'text-red-500' : 'text-orange-500'
                  }`}>
                    <Calendar className="w-3 h-3" />
                    {new Date(alumni.job_deadline) < new Date() ? 'Expired' : 'Deadline'}: {formatDate(alumni.job_deadline)}
                  </span>
                )}
              </div>
            </div>

            {alumni.job_description && (
              <p className="text-sm text-gray-700 leading-relaxed line-clamp-4 whitespace-pre-line">{alumni.job_description}</p>
            )}

            {alumni.job_requirements && alumni.job_requirements.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Requirements</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {alumni.job_requirements.map((r, i) => <li key={i} className="text-sm text-gray-700">{r}</li>)}
                </ul>
              </div>
            )}

            {alumni.skills_required && alumni.skills_required.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Skills Required</p>
                <div className="flex flex-wrap gap-1.5">
                  {alumni.skills_required.map((s, i) => (
                    <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">{s}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex items-center justify-between gap-3 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-200 transition">Close</button>
          <button
            onClick={() => { onClose(); onRequestFeedback() }}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition"
          >
            <Send className="w-4 h-4" /> Request Feedback
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Send Request Modal ──────────────────────────────────────
interface RequestModalProps {
  alumni: HiredAlumniForFeedback
  onClose: () => void
  onSent: () => void
}

const RequestModal: React.FC<RequestModalProps> = ({ alumni, onClose, onSent }) => {
  const [relationship, setRelationship] = useState<FeedbackRelationship>('employee')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)

  const handleSend = async () => {
    setSending(true)
    try {
      const { data, error } = await supabaseClient.rpc('send_feedback_request', {
        p_alumni_id: alumni.alumni_id,
        p_relationship: relationship,
        p_message: message || null,
        p_job_id: alumni.job_id || null,
      })
      if (error) throw error
      const result = data as { success: boolean; error?: string }
      if (!result.success) throw new Error(result.error || 'Failed to send request')
      toast.success('Feedback request sent!')
      onSent()
    } catch (err: any) {
      toast.error(err.message ?? 'Failed to send request')
    } finally {
      setSending(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Send className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Request Feedback</h3>
              <p className="text-sm text-gray-500">From {fullName(alumni.first_name, alumni.last_name)}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition"><X className="w-5 h-5 text-gray-400" /></button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Alumni summary */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
            {alumni.profile_picture ? (
              <img src={alumni.profile_picture} className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center text-sm font-bold text-blue-700">
                {initials(alumni.first_name, alumni.last_name)}
              </div>
            )}
            <div>
              <p className="font-semibold text-gray-900">{fullName(alumni.first_name, alumni.last_name)}</p>
              <p className="text-xs text-gray-500">{alumni.program}{alumni.year_graduated ? ` · ${alumni.year_graduated}` : ''}</p>
            </div>
          </div>

          {/* Job posting info */}
          <div className="p-3 border border-gray-200 rounded-xl space-y-1.5">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                <Briefcase className="w-4 h-4 text-blue-600" /> {alumni.job_title}
              </p>
              {alumni.job_deleted ? (
                <span className="flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                  <AlertTriangle className="w-3 h-3" /> Deleted
                </span>
              ) : alumni.job_status === 'closed' ? (
                <span className="flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  <XCircle className="w-3 h-3" /> Closed
                </span>
              ) : alumni.job_status === 'active' ? (
                <span className="flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                  <CheckCircle className="w-3 h-3" /> Active
                </span>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
              {alumni.employment_type && <span>{TYPE_LABELS[alumni.employment_type] ?? alumni.employment_type}</span>}
              {alumni.job_location && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {alumni.job_location}</span>}
              {alumni.hired_at && <span>Hired {formatDate(alumni.hired_at)}</span>}
            </div>
          </div>

          {/* Relationship */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
            <div className="flex gap-2">
              {(['employee', 'intern'] as FeedbackRelationship[]).map(r => (
                <button
                  key={r}
                  onClick={() => setRelationship(r)}
                  className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium border transition ${
                    relationship === r
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {RELATIONSHIP_LABELS[r]}
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Personal Note (optional)</label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Add a personal note to your request…"
              rows={3}
              maxLength={500}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 justify-end p-5 border-t">
          <button onClick={onClose} disabled={sending} className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition disabled:opacity-50">Cancel</button>
          <button onClick={handleSend} disabled={sending} className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition disabled:opacity-50">
            {sending ? 'Sending…' : 'Send Request'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── View Request Detail Modal ───────────────────────────────
interface ViewRequestModalProps {
  request: FeedbackRequest
  onClose: () => void
}

const ViewRequestModal: React.FC<ViewRequestModalProps> = ({ request, onClose }) => {
  const sc = STATUS_CONFIG[request.status] ?? STATUS_CONFIG.pending
  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {request.alumni_profile_picture ? (
              <img src={request.alumni_profile_picture} className="w-11 h-11 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-700 flex-shrink-0">
                {initials(request.alumni_first_name, request.alumni_last_name)}
              </div>
            )}
            <div className="min-w-0">
              <h3 className="text-base font-bold text-gray-900 truncate">{fullName(request.alumni_first_name, request.alumni_last_name)}</h3>
              <p className="text-xs text-gray-500">{request.alumni_program}{request.alumni_year_graduated ? ` · Class of ${request.alumni_year_graduated}` : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${sc.bg} ${sc.color}`}>
              {sc.icon} {sc.label}
            </span>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition"><X className="w-5 h-5 text-gray-400" /></button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* ─ Alumni info ─ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Alumni Info</h4>
              <div className="space-y-1.5 text-sm">
                {request.alumni_email && (
                  <p className="flex items-center gap-2 text-gray-600">
                    <Mail className="w-4 h-4 text-blue-400 flex-shrink-0" /> {request.alumni_email}
                  </p>
                )}
                {request.alumni_phone && (
                  <p className="flex items-center gap-2 text-gray-600">
                    <Phone className="w-4 h-4 text-blue-400 flex-shrink-0" /> {request.alumni_phone}
                  </p>
                )}
                {request.alumni_department && (
                  <p className="flex items-center gap-2 text-gray-600">
                    <GraduationCap className="w-4 h-4 text-blue-400 flex-shrink-0" /> {request.alumni_department}
                  </p>
                )}
                <p className="flex items-center gap-2 text-gray-600">
                  <User className="w-4 h-4 text-blue-400 flex-shrink-0" />
                  {RELATIONSHIP_LABELS[request.relationship]} at {request.company_name ?? '—'}
                </p>
              </div>
            </div>

            {/* Alumni skills */}
            {request.alumni_skills && request.alumni_skills.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Skills</h4>
                <div className="flex flex-wrap gap-1.5">
                  {request.alumni_skills.map((s: string) => (
                    <span key={s} className="bg-blue-50 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-md">{s}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ─ Job posting ─ */}
          <div className="border border-gray-100 rounded-xl bg-gray-50 p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <h4 className="text-sm font-semibold text-gray-800">Job Position</h4>
              {request.job_deleted ? (
                <span className="flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full flex-shrink-0">
                  <AlertTriangle className="w-3 h-3" /> Deleted
                </span>
              ) : request.job_status === 'closed' ? (
                <span className="flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full flex-shrink-0">
                  <XCircle className="w-3 h-3" /> Closed
                </span>
              ) : request.job_status === 'active' ? (
                <span className="flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full flex-shrink-0">
                  <CheckCircle className="w-3 h-3" /> Active
                </span>
              ) : null}
            </div>

            <div>
              <p className="font-bold text-gray-900 text-base">{request.job_title ?? 'Unknown Position'}</p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-1.5">
                {request.job_employment_type && (
                  <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                    {TYPE_LABELS[request.job_employment_type] ?? request.job_employment_type}
                  </span>
                )}
                {request.job_experience_level && (
                  <span>{EXPERIENCE_LEVEL_LABELS[request.job_experience_level as keyof typeof EXPERIENCE_LEVEL_LABELS] ?? request.job_experience_level}</span>
                )}
                {request.job_location && (
                  <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3 text-gray-400" />{request.job_location}</span>
                )}
                {request.job_salary_range && (
                  <span className="text-green-700 font-medium">{SALARY_LABELS[request.job_salary_range] ?? request.job_salary_range}</span>
                )}
                {request.job_deadline && (
                  <span className={`flex items-center gap-0.5 font-medium ${
                    new Date(request.job_deadline) < new Date() ? 'text-red-500' : 'text-orange-500'
                  }`}>
                    <Calendar className="w-3 h-3" />
                    {new Date(request.job_deadline) < new Date() ? 'Expired' : 'Deadline'}: {formatDate(request.job_deadline)}
                  </span>
                )}
              </div>
            </div>

            {request.job_description && (
              <p className="text-sm text-gray-700 leading-relaxed line-clamp-4 whitespace-pre-line">{request.job_description}</p>
            )}

            {request.job_requirements && request.job_requirements.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Requirements</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {request.job_requirements.map((r, i) => <li key={i} className="text-sm text-gray-700">{r}</li>)}
                </ul>
              </div>
            )}

            {request.job_skills_required && request.job_skills_required.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Skills Required</p>
                <div className="flex flex-wrap gap-1.5">
                  {request.job_skills_required.map((s, i) => (
                    <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">{s}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ─ Request details ─ */}
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Request Details</h4>
            <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <span>Requested on <span className="font-medium text-gray-900">{formatDate(request.requested_at)}</span></span>
              </div>
              {request.responded_at && (
                <div className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0" />
                  <span>Responded on <span className="font-medium text-gray-900">{formatDate(request.responded_at)}</span></span>
                </div>
              )}
              {request.completed_at && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Star className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                  <span>Feedback submitted on <span className="font-medium text-gray-900">{formatDate(request.completed_at)}</span></span>
                </div>
              )}
            </div>
          </div>

          {/* ─ Personal message ─ */}
          {request.message && (
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Your Message</h4>
              <p className="text-sm text-gray-600 bg-blue-50 rounded-xl p-4 italic">"{request.message}"</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-end flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-200 transition">Close</button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── View Feedback Modal ──────────────────────────────────────
interface ViewFeedbackModalProps {
  feedback: AlumniEmployerFeedback
  onClose: () => void
}

const ViewFeedbackModal: React.FC<ViewFeedbackModalProps> = ({ feedback, onClose }) => {
  const categories = [
    { label: 'Work Environment', value: feedback.work_environment },
    { label: 'Mentorship',       value: feedback.mentorship },
    { label: 'Career Growth',    value: feedback.career_growth },
    { label: 'Compensation',     value: feedback.compensation },
    { label: 'Overall Rating',   value: feedback.overall_rating },
  ]
  const avg = categories.reduce((s, c) => s + c.value, 0) / categories.length

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <Star className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Feedback Details</h3>
              <p className="text-sm text-gray-500">
                From {feedback.is_anonymous ? 'Anonymous' : fullName(feedback.alumni_first_name, feedback.alumni_last_name)} · {RELATIONSHIP_LABELS[feedback.relationship] ?? feedback.relationship}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition"><X className="w-5 h-5 text-gray-400" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Job details */}
          {feedback.job_title ? (
            <div className="border border-gray-100 rounded-xl bg-gray-50 p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                  <Briefcase className="w-4 h-4 text-blue-500 shrink-0" /> Job Position
                </h4>
                {feedback.job_deleted ? (
                  <span className="flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full shrink-0">
                    <AlertTriangle className="w-3 h-3" /> Deleted
                  </span>
                ) : feedback.job_status === 'closed' ? (
                  <span className="flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full shrink-0">
                    <XCircle className="w-3 h-3" /> Closed
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full shrink-0">
                    <CheckCircle className="w-3 h-3" /> Active
                  </span>
                )}
              </div>
              <div>
                <p className="font-bold text-gray-900">{feedback.job_title}</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-1.5">
                  {feedback.job_employment_type && (
                    <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                      {TYPE_LABELS[feedback.job_employment_type] ?? feedback.job_employment_type}
                    </span>
                  )}
                  {feedback.job_experience_level && <span>{EXPERIENCE_LEVEL_LABELS[feedback.job_experience_level as keyof typeof EXPERIENCE_LEVEL_LABELS] ?? feedback.job_experience_level}</span>}
                  {feedback.job_location && (
                    <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3 text-gray-400" />{feedback.job_location}</span>
                  )}
                  {feedback.job_salary_range && (
                    <span className="text-green-700 font-medium">{SALARY_LABELS[feedback.job_salary_range] ?? feedback.job_salary_range}</span>
                  )}
                  {feedback.job_deadline && (
                    <span className={`flex items-center gap-0.5 font-medium ${new Date(feedback.job_deadline) < new Date() ? 'text-red-500' : 'text-orange-500'}`}>
                      <Calendar className="w-3 h-3" />
                      {new Date(feedback.job_deadline) < new Date() ? 'Expired' : 'Deadline'}: {formatDate(feedback.job_deadline)}
                    </span>
                  )}
                </div>
              </div>
              {feedback.job_description && (
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{feedback.job_description}</p>
              )}
              {feedback.job_requirements && feedback.job_requirements.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Requirements</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    {feedback.job_requirements.map((r, i) => <li key={i} className="text-sm text-gray-600">{r}</li>)}
                  </ul>
                </div>
              )}
              {feedback.job_skills_required && feedback.job_skills_required.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Skills Required</p>
                  <div className="flex flex-wrap gap-1.5">
                    {feedback.job_skills_required.map((s, i) => (
                      <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-400 text-sm bg-gray-50 rounded-xl">
              <Briefcase className="w-7 h-7 mx-auto mb-1.5 text-gray-300" />
              No job details linked to this feedback.
            </div>
          )}

          {/* Average */}
          <div className="text-center p-4 bg-blue-50 rounded-xl">
            <p className="text-3xl font-bold text-blue-900">{avg.toFixed(1)}</p>
            <Stars value={Math.round(avg)} />
            <p className="text-xs text-blue-600 mt-1">Average Rating</p>
          </div>

          {/* Category ratings */}
          <div className="space-y-3">
            {categories.map(c => (
              <div key={c.label} className="flex items-center justify-between">
                <span className="text-sm text-gray-700">{c.label}</span>
                <div className="flex items-center gap-2">
                  <Stars value={c.value} />
                  <span className="text-sm font-medium text-gray-900 w-5 text-right">{c.value}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Recommend */}
          <div className="flex items-center gap-2 p-3 rounded-xl bg-gray-50">
            {feedback.would_recommend ? (
              <><ThumbsUp className="w-5 h-5 text-green-600" /><span className="text-sm text-green-700 font-medium">Would recommend this company</span></>
            ) : (
              <><ThumbsDown className="w-5 h-5 text-red-600" /><span className="text-sm text-red-700 font-medium">Would not recommend</span></>
            )}
          </div>

          {/* Text fields */}
          {feedback.overall_comment && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-1">Overall Comment</h4>
              <p className="text-sm text-gray-600 bg-gray-50 rounded-xl p-3">{feedback.overall_comment}</p>
            </div>
          )}
          {feedback.strengths && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-1">Strengths</h4>
              <p className="text-sm text-gray-600 bg-green-50 rounded-xl p-3">{feedback.strengths}</p>
            </div>
          )}
          {feedback.improvements && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-1">Areas for Improvement</h4>
              <p className="text-sm text-gray-600 bg-yellow-50 rounded-xl p-3">{feedback.improvements}</p>
            </div>
          )}
        </div>

        <div className="p-5 border-t flex justify-between items-center">
          <span className="text-xs text-gray-400">Submitted {formatDate(feedback.submitted_at)}</span>
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-200 transition">Close</button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Main Page ────────────────────────────────────────────────
type Tab = 'request' | 'sent' | 'received'

export default function EmployerFeedbackPage() {
  const { user } = useAuthStore()
  const [tab, setTab] = useState<Tab>('request')
  const [search, setSearch] = useState('')

  // Data
  const [hiredAlumni, setHiredAlumni] = useState<HiredAlumniForFeedback[]>([])
  const [requestsSent, setRequestsSent] = useState<FeedbackRequest[]>([])
  const [feedbackReceived, setFeedbackReceived] = useState<AlumniEmployerFeedback[]>([])
  const [loading, setLoading] = useState(true)

  // Modals
  const [requestTarget, setRequestTarget] = useState<HiredAlumniForFeedback | null>(null)
  const [viewHiredAlumni, setViewHiredAlumni] = useState<HiredAlumniForFeedback | null>(null)
  const [viewRequest, setViewRequest] = useState<FeedbackRequest | null>(null)
  const [viewFeedback, setViewFeedback] = useState<AlumniEmployerFeedback | null>(null)

  const fetchAll = useCallback(async () => {
    if (!user?.id) return
    setLoading(true)
    try {
      const [{ data: alumni }, { data: sent }, { data: received }] = await Promise.all([
        supabaseClient.rpc('get_hired_alumni_for_feedback'),
        supabaseClient.rpc('get_feedback_requests_sent'),
        supabaseClient.rpc('get_feedback_received'),
      ])
      setHiredAlumni((alumni as HiredAlumniForFeedback[]) ?? [])
      setRequestsSent((sent as FeedbackRequest[]) ?? [])
      setFeedbackReceived((received as AlumniEmployerFeedback[]) ?? [])
    } catch {
      toast.error('Failed to load feedback data')
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Counts
  const pendingCount = requestsSent.filter(r => r.status === 'pending').length
  const completedCount = requestsSent.filter(r => r.status === 'completed').length

  // Filtered lists
  const filteredAlumni = hiredAlumni.filter(a => {
    const q = search.toLowerCase()
    return !q || fullName(a.first_name, a.last_name).toLowerCase().includes(q) || a.program?.toLowerCase().includes(q) || a.job_title?.toLowerCase().includes(q)
  })

  const filteredSent = requestsSent.filter(r => {
    const q = search.toLowerCase()
    return !q || fullName(r.alumni_first_name, r.alumni_last_name).toLowerCase().includes(q)
  })

  const filteredFeedback = feedbackReceived.filter(f => {
    const q = search.toLowerCase()
    return !q || fullName(f.alumni_first_name, f.alumni_last_name).toLowerCase().includes(q)
  })

  const TABS: { key: Tab; label: string; count?: number }[] = [
    { key: 'request', label: 'Request Feedback', count: hiredAlumni.length },
    { key: 'sent',    label: 'Requests Sent',    count: requestsSent.length },
    { key: 'received', label: 'Feedback Received', count: feedbackReceived.length },
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Hero Banner ─────────────────────────────────────── */}
      <div className="bg-blue-900 text-white rounded-2xl overflow-hidden">
        <div className="px-6 sm:px-8 py-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4" />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-2">
              <HeartHandshake className="w-8 h-8 text-yellow-400" />
              <h1 className="text-2xl sm:text-3xl font-bold">Feedback & Support</h1>
            </div>
            <p className="text-blue-200 max-w-xl">Request feedback from alumni you've hired and view their ratings about their experience at your company.</p>
            {/* Stat pills */}
            <div className="flex flex-wrap gap-3 mt-4">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm">
                <Send className="w-4 h-4 text-yellow-400" />
                <span>{pendingCount} Pending</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span>{completedCount} Completed</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm">
                <Star className="w-4 h-4 text-yellow-400" />
                <span>{feedbackReceived.length} Feedback{feedbackReceived.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                tab === t.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t.label}
              {t.count !== undefined && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${tab === t.key ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-500'}`}>{t.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name…"
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* ─── Request Tab ──────────────────────────────── */}
            {tab === 'request' && (
              filteredAlumni.length === 0 ? (
                <div className="text-center py-16">
                  <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No hired alumni available for feedback</p>
                  <p className="text-gray-400 text-sm mt-1">Alumni you've hired through your job listings will appear here.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredAlumni.map(a => (
                    <div
                      key={`${a.alumni_id}-${a.job_id}`}
                      onClick={() => setViewHiredAlumni(a)}
                      className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:border-blue-200 transition cursor-pointer p-5 flex flex-col gap-3"
                    >
                      {/* Job posting block */}
                      <div className="bg-gray-50 rounded-xl px-3 py-2.5">
                        <p className="font-semibold text-gray-900 text-sm truncate flex items-center gap-1.5">
                          <Briefcase className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                          {a.job_title}
                          {a.job_deleted && <span className="text-red-400 text-xs font-normal">(Deleted)</span>}
                          {!a.job_deleted && a.job_status === 'closed' && <span className="text-gray-400 text-xs font-normal">(Closed)</span>}
                        </p>
                        {a.job_location && (
                          <p className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                            <MapPin className="w-3 h-3" /> {a.job_location}
                          </p>
                        )}
                      </div>

                      {/* Alumni row */}
                      <div className="flex items-center gap-2.5">
                        {a.profile_picture ? (
                          <img src={a.profile_picture} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-700 flex-shrink-0">
                            {initials(a.first_name, a.last_name)}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{fullName(a.first_name, a.last_name)}</p>
                          <p className="text-xs text-gray-500">{a.program}{a.year_graduated ? ` · ${a.year_graduated}` : ''}</p>
                        </div>
                      </div>

                      {/* View details hint */}
                      <div className="flex items-center gap-1.5 text-xs text-blue-500 font-medium mt-auto pt-1 border-t border-gray-100">
                        <Eye className="w-3.5 h-3.5" /> View details & request feedback
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}

            {/* ─── Sent Tab ─────────────────────────────────── */}
            {tab === 'sent' && (
              filteredSent.length === 0 ? (
                <div className="text-center py-16">
                  <Send className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No requests sent yet</p>
                  <p className="text-gray-400 text-sm mt-1">Go to the "Request Feedback" tab to get started.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredSent.map(r => {
                    const sc = STATUS_CONFIG[r.status] ?? STATUS_CONFIG.pending
                    return (
                      <div
                        key={r.id}
                        onClick={() => setViewRequest(r)}
                        className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:border-blue-200 transition cursor-pointer p-5 flex flex-col gap-3"
                      >
                        {/* Status badge + date */}
                        <div className="flex items-center justify-between">
                          <span className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${sc.bg} ${sc.color}`}>
                            {sc.icon} {sc.label}
                          </span>
                          <span className="text-xs text-gray-400">{formatDate(r.requested_at)}</span>
                        </div>

                        {/* Job posting block */}
                        <div className="bg-gray-50 rounded-xl px-3 py-2.5">
                          <p className="font-semibold text-gray-900 text-sm truncate flex items-center gap-1.5">
                            <Briefcase className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                            {r.job_title ?? 'Unknown Position'}
                            {r.job_deleted && <span className="text-red-400 text-xs font-normal">(Deleted)</span>}
                            {!r.job_deleted && r.job_status === 'closed' && <span className="text-gray-400 text-xs font-normal">(Closed)</span>}
                          </p>
                          {(r.job_employment_type || r.job_location) && (
                            <div className="flex flex-wrap gap-x-3 mt-1 text-xs text-gray-400">
                              {r.job_employment_type && <span>{TYPE_LABELS[r.job_employment_type] ?? r.job_employment_type}</span>}
                              {r.job_location && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{r.job_location}</span>}
                            </div>
                          )}
                        </div>

                        {/* Alumni row */}
                        <div className="flex items-center gap-2.5">
                          {r.alumni_profile_picture ? (
                            <img src={r.alumni_profile_picture} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-700 flex-shrink-0">
                              {initials(r.alumni_first_name, r.alumni_last_name)}
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{fullName(r.alumni_first_name, r.alumni_last_name)}</p>
                            <p className="text-xs text-gray-500">{r.alumni_program} · {RELATIONSHIP_LABELS[r.relationship]}</p>
                          </div>
                        </div>

                        {/* View details hint */}
                        <div className="flex items-center gap-1.5 text-xs text-blue-500 font-medium mt-auto pt-1 border-t border-gray-100">
                          <Eye className="w-3.5 h-3.5" /> View details
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            )}

            {/* ─── Received Tab ─────────────────────────────── */}
            {tab === 'received' && (
              filteredFeedback.length === 0 ? (
                <div className="text-center py-16">
                  <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No feedback received yet</p>
                  <p className="text-gray-400 text-sm mt-1">Feedback from alumni will appear here after they submit.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredFeedback.map(f => {
                    const avg = (f.work_environment + f.mentorship + f.career_growth + f.compensation + f.overall_rating) / 5
                    return (
                      <div key={f.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition p-5 flex flex-col">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3 min-w-0">
                            {!f.is_anonymous && f.alumni_profile_picture ? (
                              <img src={f.alumni_profile_picture} className="w-10 h-10 rounded-full object-cover" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-bold text-gray-500">
                                {f.is_anonymous ? '?' : initials(f.alumni_first_name, f.alumni_last_name)}
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 truncate">{f.is_anonymous ? 'Anonymous' : fullName(f.alumni_first_name, f.alumni_last_name)}</p>
                              <p className="text-xs text-gray-500">{RELATIONSHIP_LABELS[f.relationship] ?? f.relationship}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 bg-yellow-50 text-yellow-700 font-bold text-sm px-2.5 py-1 rounded-full">
                            <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" /> {avg.toFixed(1)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mb-3">
                          <Stars value={Math.round(avg)} />
                          <span className="text-xs text-gray-400">{formatDate(f.submitted_at)}</span>
                        </div>
                        {f.job_title && (
                          <div className="flex items-start gap-2 bg-blue-50 rounded-xl px-3 py-2.5 mb-3">
                            <Briefcase className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-blue-900 truncate">{f.job_title}</p>
                              <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5 text-xs text-blue-700">
                                {f.job_employment_type && <span>{TYPE_LABELS[f.job_employment_type] ?? f.job_employment_type}</span>}
                                {f.job_experience_level && <span>· {EXPERIENCE_LEVEL_LABELS[f.job_experience_level as keyof typeof EXPERIENCE_LEVEL_LABELS] ?? f.job_experience_level}</span>}
                                {f.job_location && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{f.job_location}</span>}
                              </div>
                            </div>
                          </div>
                        )}
                        {f.overall_comment && <p className="text-sm text-gray-600 line-clamp-2 mb-3">{f.overall_comment}</p>}
                        <button
                          onClick={() => setViewFeedback(f)}
                          className="w-full flex items-center justify-center gap-2 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-xl transition mt-auto"
                        >
                          <Eye className="w-4 h-4" /> View Details
                        </button>
                      </div>
                    )
                  })}
                </div>
              )
            )}
          </>
        )}
      </div>

      {/* ── Modals ──────────────────────────────────────────── */}
      {viewHiredAlumni && (
        <ViewHiredAlumniModal
          alumni={viewHiredAlumni}
          onClose={() => setViewHiredAlumni(null)}
          onRequestFeedback={() => { setRequestTarget(viewHiredAlumni); setViewHiredAlumni(null) }}
        />
      )}
      {requestTarget && (
        <RequestModal
          alumni={requestTarget}
          onClose={() => setRequestTarget(null)}
          onSent={() => { setRequestTarget(null); fetchAll() }}
        />
      )}
      {viewRequest && (
        <ViewRequestModal
          request={viewRequest}
          onClose={() => setViewRequest(null)}
        />
      )}
      {viewFeedback && (
        <ViewFeedbackModal
          feedback={viewFeedback}
          onClose={() => setViewFeedback(null)}
        />
      )}
    </div>
  )
}
